"use client";

import { useEffect, useRef, useState } from "react";
import { resolvePath, resolveText, resolveUnknown } from "./binding";
import { resolveMacaronIconComponent } from "./macaronIconRegistry";
import type { MacaronRenderContext, RecordAny } from "./types";

function resolveOptionalBool(expr: unknown, data: RecordAny): boolean | undefined {
  const value = resolveUnknown(expr, data);
  return typeof value === "boolean" ? value : undefined;
}

export function MacaronPhotoInput({ config, ctx }: { config: RecordAny; ctx: MacaronRenderContext }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [preview, setPreview] = useState<string>("");
  const [retryHint, setRetryHint] = useState<string | null>(null);
  const valuePath = resolvePath(config.value);
  const hasValuePath = resolvePath(config.hasValue);
  const isUploadingPath = resolvePath(config.isUploading);
  const remoteUrl = resolveText(config.value, ctx.data);
  const remoteUploading = resolveOptionalBool(config.isUploading, ctx.data) === true;
  const placeholder = resolveText(config.placeholder, ctx.data) || "Snap a pic";
  const previewUrl = preview || remoteUrl;
  const cameraIcon = resolveMacaronIconComponent("camera", "line");

  useEffect(() => () => {
    if (preview.startsWith("blob:")) URL.revokeObjectURL(preview);
  }, [preview]);

  return (
    <div
      className="relative h-[150px] w-full cursor-pointer overflow-hidden rounded-[12px] border border-black/10 bg-[#E9E7E2]"
      onClick={() => {
        if (!remoteUploading) inputRef.current?.click();
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (remoteUploading) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
    >
      {previewUrl ? (
        <img src={previewUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-[10px] text-[#161615]">
          <div className="inline-flex h-8 w-8 items-center justify-center">
            {cameraIcon ? <cameraIcon.Component data-macaron-icon={cameraIcon.canonicalName} data-macaron-icon-style="line" className="h-6 w-6" /> : <div className="text-[24px] leading-none">+</div>}
          </div>
          <div className="text-[14px] leading-[1.71]">{placeholder}</div>
        </div>
      )}
      {remoteUploading ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-[10px] bg-black/30 text-white">
          <div className="h-[22px] w-[22px] animate-spin rounded-full border-[2.4px] border-white/35 border-t-white" />
          <div className="text-[13px] font-medium">Uploading...</div>
        </div>
      ) : retryHint ? (
        <div className="absolute inset-x-0 bottom-0 bg-black/45 px-3 py-2 text-center text-[12px] font-medium text-white">{retryHint}</div>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.currentTarget.value = "";
          if (!file) {
            setRetryHint("Selection canceled. Tap to retry.");
            return;
          }
          if (preview.startsWith("blob:")) URL.revokeObjectURL(preview);
          const url = URL.createObjectURL(file);
          setPreview(url);
          setRetryHint(null);
          if (valuePath) ctx.updateData?.(valuePath, url);
          if (hasValuePath) ctx.updateData?.(hasValuePath, true);
          if (isUploadingPath) ctx.updateData?.(isUploadingPath, false);
        }}
      />
    </div>
  );
}
